import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels'; // Ensure you've installed this package

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const TraficoPerAduanaBarChart = ({ data, isLoading }) => {
  // Define the colors for each aging bracket
  const agingBracketColors = {
    '0-10 days': 'rgba(0, 128, 0, 0.6)', // More vivid green
    '11-20 days': 'rgba(255, 205, 86, 0.6)', // Yellow
    '21-30 days': 'rgba(255, 159, 64, 0.6)', // Orange
    '31-40 days': 'rgba(255, 99, 132, 0.6)', // Darker orange
    '40+ days': 'rgba(255, 99, 132, 0.9)' // Red
  };

  const [aduanaData, setAduanaData] = useState({});

  useEffect(() => {
    const processAduanaData = () => {
      const tempAduanaData = data.reduce((acc, item) => {
        const { Aduana, PedimentoFecha } = item;
        const agingBracket = calculateAging(PedimentoFecha);
        acc[Aduana] = acc[Aduana] || {};
        acc[Aduana][agingBracket] = (acc[Aduana][agingBracket] || 0) + 1;
        return acc;
      }, {});
      setAduanaData(tempAduanaData);
    };

    if (!isLoading && data && data.length > 0) {
      processAduanaData();
    }
  }, [data, isLoading]);

  const calculateAging = (pedimentoFecha) => {
    const today = new Date();
    const date = new Date(pedimentoFecha);
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 10) return '0-10 days';
    if (diffDays <= 20) return '11-20 days';
    if (diffDays <= 30) return '21-30 days';
    if (diffDays <= 40) return '31-40 days';
    return '40+ days';
  };

  const sortedAduanas = Object.entries(aduanaData)
    .sort((a, b) => Object.values(b[1]).reduce((sum, n) => sum + n, 0) - Object.values(a[1]).reduce((sum, n) => sum + n, 0))
    .map(entry => entry[0]);

  const agingBrackets = ['0-10 days', '11-20 days', '21-30 days', '31-40 days', '40+ days'];

  const chartData = {
    labels: sortedAduanas,
    datasets: agingBrackets.map((bracket, index) => ({
      label: bracket,
      data: sortedAduanas.map(aduana => aduanaData[aduana]?.[bracket] || 0),
      backgroundColor: agingBracketColors[bracket],
    })),
  };

  const options = {
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
      },
      x: {
        stacked: true,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
      },
      datalabels: {
        color: '#fff',
        display: true,
        formatter: (value, context) => {
          return value > 0 ? value : '';
        },
      }
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-blue-600 mr-2" />
          Trafico Count per Aduana
        </h2>
      </div>
      <div className="flex-grow relative">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
            <span className="ml-2 text-lg">Loading...</span>
          </div>
        ) : (
          <Bar data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default TraficoPerAduanaBarChart;
